'use client';

// Package Imports
import React, {useRef, useState, useEffect, useMemo, useCallback, FormEvent } from 'react';
import { useRouter } from 'next/navigation';
import { StripePaymentElementOptions } from '@stripe/stripe-js';
import {
  useStripe,
  useElements,
  PaymentElement
} from '@stripe/react-stripe-js';
import { useSession, getSession } from 'next-auth/react';
import { v4 as uuidv4 } from 'uuid';

// Components Imports
import CartCheckoutCoupons from '@/components/cartCheckout/cartCheckoutPromoCodes';
import CartCheckoutDisclosures from '@/components/cartCheckout/cartCheckoutDisclosures';
import CartCheckoutLogin from '@/components/cartCheckout/cartCheckoutLogin';
import CartCheckoutTemporaryAuthorization from '@/components/cartCheckout/cartCheckoutTemporaryAuthorization';
import CartCheckoutTotals from '@/components/cartCheckout/cartCheckoutTotals';
import CheckoutModal from "@/components/checkout/checkoutModal";
import Modal from "@/components/modal";
import Spinner from '@/components/spinner';
import StylizedSelect from '@/components/stylizedSelect';
import StylizedButton from '@/components/stylizedButton';
import Toast from "@/components/toast";

// Constants Imports
import { OrderCompleteResponseKey, REGEX } from '@/lib/constants/common';

// Context Imports
import { useOrder } from '@/lib/context/orderContext';

// Function Imports
import { calculateTotalQuantity } from '@/lib/functions/calculateTotalQuantity';
import { encryptSessionData } from '@/lib/functions/encryptSessionData';
import {
  isValidPhoneNumber,
  normalizePhoneNumber,
} from '@/lib/functions/formatPhoneInput';
import { formatPostcode } from '@/lib/functions/formatPostcode';
import { handleCheckout } from '@/lib/functions/checkout';
import {
  markPartyOrderNoSubstitutions,
  resolvePartyAllowSubstitutionsForCartUpdate,
} from '@/lib/functions/ensurePartyOrderSubstitutions';
import {
  clearCheckoutFormDraft,
  collectCheckoutFormDraftFromForm,
  readCheckoutFormDraft,
  resolveCheckoutDraftCheckbox,
  resolveCheckoutDraftString,
  writeCheckoutFormDraft,
} from '@/lib/functions/checkoutFormDraft';
import {
  applyCheckoutFormFieldUpdates,
  buildCheckoutAccountPrefill,
  getAccountSessionFieldUpdates,
  inferEditedAccountFields,
  isCheckoutAccountPrefillField,
} from '@/lib/functions/checkoutAccountPrefill';
import type { CheckoutAccountPrefillField, CheckoutAccountPrefillSnapshot } from '@/lib/functions/checkoutAccountPrefill';
import {
  resolveCheckoutShippingDefaults,
  resolveCheckoutShippingStateCode,
  resolveCheckoutShippingCountryLabel,
} from '@/lib/functions/resolveCheckoutShippingDefaults';
import { formatProvinceCode, formatProvinceCodeForDisplay } from '@/lib/functions/formatProvinceCode';
import { setOrderDetails } from '@/lib/functions/setOrderDetails';
import { setOrderAddress } from '@/lib/functions/setOrderAddress';
import { setOrderShippingMethod } from "@/lib/functions/setOrderShippingMethod";
import {
  getShippingMethodDisplayName,
  PARTY_SUPPLIES_DOMESTIC_FEDEX_GROUND,
  resolvePartySuppliesShippingMethodsForDisplay,
} from '@/lib/functions/preferedShippingMethods';
import { isShippingQuoteUnavailable } from '@/lib/functions/isShippingQuoteUnavailable';
import { useDebounce } from "@/lib/hooks/debounce";

// Style Imports 
import styles from '../checkout.module.scss';
import formStyles from '../checkoutForms.module.scss';

// Type Imports
import { FulfillmentBillingContact } from '@/lib/types/fulfillmentBillingContact';
import type { User } from '@/lib/types/user';
import { Address } from '@/lib/types/address';
import { OrderPayload as OrderPayloadType, OrderPayload } from '@/lib/types/orderPayload';
import type { ShippingMethod } from '@/lib/types/shippingMethod';

// Constants Imports 
import { STATES_CODES } from '@/lib/constants/common';
import {
  CANADIAN_PROVINCES,
  COUNTRY_CODES,
  DEFAULT_COUNTRY_CODE,
  getPostalCodeLabel,
  getPostalCodePattern,
  getProvinceLabel,
  isCanada,
  isProvinceRequired,
  isUnitedStates,
} from '@/lib/constants/countries';

const LABELS: Record<string, string> = {
  shippingPhoneNumber: 'Phone for Texting',
  shippingFirstName: 'First Name',
  shippingLastName: 'Last Name',
  shippingStreet: 'Address',
  shippingStreet2: 'Apt/Suite',
  shippingCity: 'City',
  shippingProvinceCode: 'State',
  shippingPostCode: 'ZIP Code',
  billingFirstName: 'First Name',
  billingLastName: 'Last Name',
  billingPhoneNumber: 'Mobile Phone for Text Updates',
  billingEmail: 'Email Address',
  billingStreet: 'Address',
  billingStreet2: 'Apt/Suite',
  billingCity: 'City',
  billingProvinceCode: 'State',
  billingCountryCode: 'Country',
  billingPostCode: 'ZIP Code',
  noSubstitutions: 'I DO NOT Want Substitutions On This Order',
  orderNotes: 'Order Notes',
  ageCheck: 'I am at least 21 years of age and understand that my ID will be checked to receive my order',
  termsCheck: 'I have read and agree to the website',
};

const REQUIRED_FIELDS = [
  'shippingFirstName',
  'shippingLastName',
  'shippingPhoneNumber',
  'billingFirstName',
  'billingLastName',
  'billingPhoneNumber',
  'billingEmail',
  'billingStreet',
  'billingCity',
  'billingPostCode',
  'ageCheck',
  'termsCheck',
];

const REQUIRED_FIELDS_WITHOUT_CHECK = [
  'shippingFirstName',
  'shippingLastName',
  'shippingPhoneNumber',
  'billingFirstName',
  'billingLastName',
  'billingPhoneNumber',
  'billingEmail',
  'billingStreet',
  'billingCity',
  'billingPostCode',
];

// Trailing debounce for billing/shipping contact sync; matches specs-react checkout.
const CHECKOUT_ADDRESS_SYNC_DEBOUNCE_MS = 1000;

const buildCheckoutShippingSnapshot = (
  formData: FormData,
  shippingStateCode: string,
  shippingCountryCode: string,
) => JSON.stringify({
  firstName: (formData.get('shippingFirstName') as string) || '',
  lastName: (formData.get('shippingLastName') as string) || '',
  phoneNumber: (formData.get('shippingPhoneNumber') as string) || '',
  street: (formData.get('shippingStreet') as string) || '',
  street2: (formData.get('shippingStreet2') as string) || '',
  city: (formData.get('shippingCity') as string) || '',
  postcode: (formData.get('shippingPostCode') as string) || '',
  provinceCode: shippingStateCode,
  countryCode: shippingCountryCode,
});

const CHECKOUT_ADDRESS_FIELD_IDS = [
  'shippingFirstName',
  'shippingLastName',
  'shippingPhoneNumber',
  'billingFirstName',
  'billingLastName',
  'billingPhoneNumber',
  'billingEmail',
  'billingStreet',
  'billingStreet2',
  'billingCity',
  'billingProvinceCode',
  'billingPostCode',
] as const;

const buildCheckoutAddressSignature = (
  formData: FormData,
  billingStateCode: string,
  billingCountryCode: string,
  shippingStateCode: string,
  shippingCountryCode: string,
) => JSON.stringify({
  billingStateCode,
  billingCountryCode,
  fieldValues: CHECKOUT_ADDRESS_FIELD_IDS.map((field) => (formData.get(field) as string) || ''),
  shippingSnapshot: buildCheckoutShippingSnapshot(formData, shippingStateCode, shippingCountryCode),
});

interface CheckoutInnerProps {
  orderData: OrderPayload,
  refreshCart: (showLoader: boolean) => void;
  setOrderData: React.Dispatch<React.SetStateAction<OrderPayloadType | null>>;
  onOpenAddressModal: () => void;
  onEditCart: () => void;
  displayedShippingMethods: ShippingMethod[];
  shippingMethodsLoading: boolean;
  hasLoadedShippingMethods: boolean;
}

export interface CartItem {
  'quantity': number,
  'code': string
}

interface ToastState {
  id: string;
  type: 'error' | 'success' | 'notice';
  message: string;
}

export default function CheckoutInner({
  orderData,
  refreshCart,
  setOrderData,
  onOpenAddressModal,
  onEditCart,
  displayedShippingMethods,
  shippingMethodsLoading,
  hasLoadedShippingMethods,
}: CheckoutInnerProps) {
  const router = useRouter();
  const formRef = useRef<HTMLFormElement>(null);
  const hasLoggedRequiredRef = useRef(false);
  const lastCouponCodeRef = useRef<string | null | undefined>(orderData?.pricing?.appliedCouponCode);
  const checkRequiredFieldsRef = useRef<(() => void) | null>(null);
  const lastAddressSignatureRef = useRef<string>('');
  // Trailing debounce timer for address sync so typing does not trigger an API call per keystroke.
  const addressUpdateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Monotonic request id so stale/in-flight responses cannot overwrite newer updates.
  const addressUpdateRequestIdRef = useRef(0);
  const lastSyncedShippingSnapshotRef = useRef<string>('');
  const isClient = typeof window !== 'undefined' && window !== undefined;
  const { data: session, status } = useSession();
  const wasAuthenticatedRef = useRef(status === 'authenticated');
  const debounceClick = useDebounce(2000);

  // Context
  const { state: orderState, dispatch: orderDispatch } = useOrder();

  // Stipe
  const stripe = useStripe();
  const elements = useElements();

  // State
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [isUpdatingSubstitutions, setIsUpdatingSubstitutions] = useState<boolean>(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submissionErrors, setSubmissionErrors] = useState<boolean>(false);
  const [paymentError, setPaymentError] = useState<boolean>(false);
  const [billingStateCode, setBillingStateCode] = useState<string>('TX');
  const [billingCountryCode, setBillingCountryCode] = useState<string>(DEFAULT_COUNTRY_CODE);
  const [shippingStateCode, setShippingStateCode] = useState<string>('TX');
  const [modalOpen, setModalOpen] = useState(false);
  const [alerts, setToasts] = useState<ToastState[]>([]);
  const [selectedShippingMethod, setSelectedShippingMethod] = useState<string | null>(
    orderData?.shipments?.[0]?.method ?? null,
  );
  const [isUpdatingShipment, setIsUpdatingShipment] = useState(false);
  const [isUpdatingAddress, setIsUpdatingAddress] = useState(false);
  const [orderIsAddressed, setOrderIsAddressed] = useState(
    () => Boolean(orderData?.states?.checkout && orderData.states.checkout !== 'cart'),
  );
  const [orderHasShipping, setOrderHasShipping] = useState(
    () => Boolean(orderData?.shipments?.[0]?.method),
  );
  const isOrderUpdating = shippingMethodsLoading || isUpdatingShipment || isUpdatingSubstitutions || isUpdatingAddress;
  const persistDraftTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const accountPrefillRef = useRef<CheckoutAccountPrefillSnapshot>({});
  const editedAccountFieldsRef = useRef<Set<CheckoutAccountPrefillField>>(new Set());
  const accountPrefillInitializedRef = useRef(false);
  const [accountSelectRevision, setAccountSelectRevision] = useState(0);

  const orderToken = orderData?.token;
  const formDraft = useMemo(
    () => (orderToken ? readCheckoutFormDraft(orderToken) : null),
    [orderToken],
  );

  const shippingDefaults = useMemo(
    () => resolveCheckoutShippingDefaults(orderData, session?.user?.shippingAddress),
    [orderData, session?.user?.shippingAddress],
  );

  const shippingCountryCode = (shippingDefaults.countryCode ?? DEFAULT_COUNTRY_CODE).toUpperCase();
  useEffect(() => {
    if (orderData?.states?.checkout && orderData.states.checkout !== 'cart') {
      setOrderIsAddressed(true);
    }
  }, [orderData?.states?.checkout]);

  useEffect(() => {
    if (orderData?.shipments?.[0]?.method) {
      setOrderHasShipping(true);
    }
  }, [orderData?.shipments?.[0]?.method]);

  const primaryShipmentId =
    Array.isArray(orderData?.shipments) && orderData.shipments.length > 0
      ? orderData.shipments[0].id
      : undefined;
  const appliedShipmentMethod = orderData?.shipments?.[0]?.method ?? null;

  const markAccountFieldEdited = useCallback((field: string) => {
    if (!isCheckoutAccountPrefillField(field)) {
      return;
    }
    if (!(field in accountPrefillRef.current)) {
      return;
    }
    editedAccountFieldsRef.current.add(field);
  }, []);

  const persistFormDraft = useCallback((overrides?: {
    billingCountryCode?: string;
    billingStateCode?: string;
    selectedShippingMethod?: string | null;
  }) => {
    if (!orderToken || !formRef.current) {
      return;
    }

    if (persistDraftTimeoutRef.current) {
      clearTimeout(persistDraftTimeoutRef.current);
    }

    persistDraftTimeoutRef.current = setTimeout(() => {
      if (!orderToken || !formRef.current) {
        return;
      }

      const draft = collectCheckoutFormDraftFromForm(formRef.current, {
        billingCountryCode: overrides?.billingCountryCode ?? billingCountryCode,
        billingStateCode: overrides?.billingStateCode ?? billingStateCode,
        selectedShippingMethod: overrides?.selectedShippingMethod ?? selectedShippingMethod,
      });
      draft.accountPrefill = accountPrefillRef.current;
      draft.editedAccountFields = Array.from(editedAccountFieldsRef.current);

      writeCheckoutFormDraft(orderToken, draft);
    }, 300);
  }, [orderToken, billingCountryCode, billingStateCode, selectedShippingMethod]);

  const syncCheckoutFromAccount = useCallback(async () => {
    if (
      !orderToken
      || status !== 'authenticated'
      || !session?.user
      || !accountPrefillInitializedRef.current
    ) {
      return;
    }

    let sessionUser = session.user as User;

    if (typeof window !== 'undefined' && sessionStorage.getItem('accountProfileUpdateSuccess') === '1') {
      const refreshedSession = await getSession();
      sessionStorage.removeItem('accountProfileUpdateSuccess');
      if (refreshedSession?.user) {
        sessionUser = refreshedSession.user as User;
      }
    }

    if (!formRef.current) {
      return;
    }

    const currentForm = collectCheckoutFormDraftFromForm(formRef.current, {
      billingCountryCode,
      billingStateCode,
      selectedShippingMethod,
    });

    const { updates, nextAccountPrefill } = getAccountSessionFieldUpdates(
      orderData,
      sessionUser,
      accountPrefillRef.current,
      editedAccountFieldsRef.current,
      currentForm,
    );

    if (Object.keys(updates).length === 0) {
      return;
    }

    applyCheckoutFormFieldUpdates(formRef.current, updates);

    if (updates.billingCountryCode) {
      setBillingCountryCode(updates.billingCountryCode);
    }
    if (updates.billingProvinceCode) {
      setBillingStateCode(updates.billingProvinceCode);
    }
    if (updates.billingCountryCode || updates.billingProvinceCode) {
      setAccountSelectRevision((revision) => revision + 1);
    }

    accountPrefillRef.current = nextAccountPrefill;

    if (persistDraftTimeoutRef.current) {
      clearTimeout(persistDraftTimeoutRef.current);
    }

    const draft = collectCheckoutFormDraftFromForm(formRef.current, {
      billingCountryCode: updates.billingCountryCode ?? billingCountryCode,
      billingStateCode: updates.billingProvinceCode ?? billingStateCode,
      selectedShippingMethod,
    });
    draft.accountPrefill = nextAccountPrefill;
    draft.editedAccountFields = Array.from(editedAccountFieldsRef.current);
    writeCheckoutFormDraft(orderToken, draft);
  }, [
    orderToken,
    orderData,
    session?.user,
    status,
    billingCountryCode,
    billingStateCode,
    selectedShippingMethod,
  ]);

  useEffect(() => {
    if (!orderToken || status === 'loading') {
      if (!orderToken) {
        accountPrefillInitializedRef.current = false;
      }
      return;
    }

    const draft = readCheckoutFormDraft(orderToken);

    if (draft?.accountPrefill && Object.keys(draft.accountPrefill).length > 0) {
      accountPrefillRef.current = draft.accountPrefill;
      editedAccountFieldsRef.current = new Set(
        draft.editedAccountFields ?? inferEditedAccountFields({
          ...draft,
          accountPrefill: draft.accountPrefill,
        }),
      );
      accountPrefillInitializedRef.current = true;
      return;
    }

    const accountPrefill = buildCheckoutAccountPrefill(orderData, session?.user);
    accountPrefillRef.current = accountPrefill;
    const editedAccountFields = inferEditedAccountFields({
      ...draft,
      accountPrefill,
    });
    editedAccountFieldsRef.current = new Set(editedAccountFields);

    writeCheckoutFormDraft(orderToken, {
      ...(draft ?? {}),
      accountPrefill,
      editedAccountFields,
    });

    accountPrefillInitializedRef.current = true;
  }, [orderToken, orderData, status, session?.user]);

  useEffect(() => {
    if (!accountPrefillInitializedRef.current || status !== 'authenticated') {
      return;
    }

    const timer = window.setTimeout(() => {
      void syncCheckoutFromAccount();
    }, 0);

    return () => window.clearTimeout(timer);
  }, [
    syncCheckoutFromAccount,
    status,
    orderToken,
    session?.user?.billingAddress,
    session?.user?.shippingAddress,
    session?.user?.email,
  ]);

  useEffect(() => {
    if (status !== 'authenticated' || wasAuthenticatedRef.current) {
      if (status === 'unauthenticated') {
        wasAuthenticatedRef.current = false;
      }
      return;
    }

    wasAuthenticatedRef.current = true;
    lastAddressSignatureRef.current = '';
    hasLoggedRequiredRef.current = false;
    void refreshCart(false);
  }, [refreshCart, status]);

  useEffect(() => {
    const handleReturnToCheckout = () => {
      void syncCheckoutFromAccount();
    };

    window.addEventListener('focus', handleReturnToCheckout);
    window.addEventListener('pageshow', handleReturnToCheckout);

    return () => {
      window.removeEventListener('focus', handleReturnToCheckout);
      window.removeEventListener('pageshow', handleReturnToCheckout);
    };
  }, [syncCheckoutFromAccount]);

  useEffect(() => {
    return () => {
      if (persistDraftTimeoutRef.current) {
        clearTimeout(persistDraftTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setShippingStateCode(resolveCheckoutShippingStateCode(shippingDefaults));
  }, [shippingDefaults]);

  useEffect(() => {
    if (formDraft?.billingCountryCode) {
      setBillingCountryCode(formDraft.billingCountryCode.toUpperCase());
    }
    if (formDraft?.billingProvinceCode) {
      setBillingStateCode(formDraft.billingProvinceCode);
    }
  }, [orderToken, formDraft?.billingCountryCode, formDraft?.billingProvinceCode]);

  useEffect(() => {
    if (formDraft?.billingCountryCode || formDraft?.billingProvinceCode) {
      return;
    }

    const sessionCountry = session?.user?.billingAddress?.countryCode ?? shippingDefaults.countryCode ?? DEFAULT_COUNTRY_CODE;
    setBillingCountryCode(sessionCountry.toUpperCase());
    setBillingStateCode(
      resolveCheckoutShippingStateCode(
        {
          provinceCode: session?.user?.billingAddress?.provinceCode ?? shippingDefaults.provinceCode,
          countryCode: sessionCountry,
        },
      ),
    );
  }, [formDraft?.billingCountryCode, formDraft?.billingProvinceCode, session?.user?.billingAddress, shippingDefaults.countryCode, shippingDefaults.provinceCode]);

  const shippingProvinceLabel = useMemo(() => {
    const provinceCode = formatProvinceCodeForDisplay(
      shippingCountryCode,
      shippingDefaults.provinceCode,
    );

    if (!provinceCode) {
      return '';
    }

    if (isUnitedStates(shippingCountryCode)) {
      return STATES_CODES.find((state) => state.value === provinceCode)?.label ?? provinceCode;
    }

    return provinceCode;
  }, [shippingCountryCode, shippingDefaults.provinceCode]);

  const shippingCountryLabel = resolveCheckoutShippingCountryLabel(shippingDefaults);
  const billingProvinceOptions = isUnitedStates(billingCountryCode)
    ? STATES_CODES
    : isCanada(billingCountryCode)
      ? CANADIAN_PROVINCES
      : [];

  const hasLockedShippingAddress = Boolean(
    shippingDefaults.street && shippingDefaults.city && shippingDefaults.postcode,
  );
  const shippingUnavailable = isShippingQuoteUnavailable(orderData?.pricing?.shipping);

  useEffect(() => {
    if (!formRef.current || !hasLockedShippingAddress || !orderIsAddressed) {
      return;
    }
    if (lastSyncedShippingSnapshotRef.current) {
      return;
    }

    lastSyncedShippingSnapshotRef.current = buildCheckoutShippingSnapshot(
      new FormData(formRef.current),
      shippingStateCode,
      shippingCountryCode,
    );
  }, [hasLockedShippingAddress, orderIsAddressed, shippingCountryCode, shippingStateCode]);

  useEffect(() => {
    const numItemsInCart = orderData ? calculateTotalQuantity(orderData.items || []) : 0;
    if (numItemsInCart < 1) {
      onEditCart();
    }
  }, [orderData, onEditCart]);

  useEffect(() => {
    const orderMethod = orderData?.shipments?.[0]?.method;

    // Order shipment method wins over a stale checkout draft (e.g. after international re-quote).
    if (orderMethod) {
      setSelectedShippingMethod(orderMethod);
      return;
    }

    if (formDraft?.selectedShippingMethod) {
      setSelectedShippingMethod(formDraft.selectedShippingMethod);
    }
  }, [orderToken, formDraft?.selectedShippingMethod, orderData?.shipments?.[0]?.method]);

  const allowSubstitutions = orderData?.settings?.allowSubstitutions ?? true;

  const shippingMethodsForDisplay = useMemo(() => {
    if (displayedShippingMethods.length === 0 && !hasLoadedShippingMethods) {
      return [];
    }

    if (shippingMethodsLoading && displayedShippingMethods.length > 0) {
      return resolvePartySuppliesShippingMethodsForDisplay(
        displayedShippingMethods,
        null,
        null,
      );
    }

    return resolvePartySuppliesShippingMethodsForDisplay(
      displayedShippingMethods,
      appliedShipmentMethod,
      orderData?.pricing?.shipping ?? null,
    );
  }, [
    displayedShippingMethods,
    appliedShipmentMethod,
    hasLoadedShippingMethods,
    shippingMethodsLoading,
    orderData?.pricing?.shipping,
  ]);

  const renderTextField = (
    id: keyof typeof LABELS,
    autoComplete: string,
    value: string = '',
    type: string = 'text',
  ) => (
    <div className={`formInput formText ${errors[id] ? 'formError' : ''}`} key={id}>
      <div>
        <label htmlFor={id}>{LABELS[id]} {REQUIRED_FIELDS.includes(id) &&
          <span className={styles.required}>*</span>}</label>
        <input
          id={id}
          name={id}
          type={type}
          autoComplete={autoComplete}
          aria-invalid={errors[id] ? 'true' : 'false'}
          aria-describedby={errors[id] ? `${id}-error` : undefined}
          onFocus={() => handleFocus(id)}
          defaultValue={value}
          onChange={() => {
            markAccountFieldEdited(id);
            persistFormDraft();
          }}
          onBlur={() => persistFormDraft()}
        />
      </div>
      {errors[id] && <span id={`${id}-error`} className={styles.error}>{errors[id]}</span>}
    </div>
  );

  const handleShippingMethodChange = async (methodCode: string) => {
    const prevMethod = orderData?.shipments?.[0]?.method ?? selectedShippingMethod ?? null;

    setSelectedShippingMethod(methodCode);
    persistFormDraft({ selectedShippingMethod: methodCode });

    const tokenValue = orderData?.token ?? orderState.token;
    const shipmentId =
      Array.isArray(orderData?.shipments) && orderData.shipments.length > 0
        ? orderData.shipments[0].id
        : undefined;

    if (!tokenValue || !shipmentId) {
      showToast('error', 'Unable to update shipping method: missing cart token or shipment.');
      setSelectedShippingMethod(prevMethod);
      return;
    }

    setIsUpdatingShipment(true);

    try {
      const shipmentResponse = await setOrderShippingMethod(tokenValue, shipmentId, methodCode);

      if (!shipmentResponse?.success) {
        setSelectedShippingMethod(prevMethod);
        showToast('error', 'We could not update your shipping method. Cart was refreshed.');
        await refreshCart(false);
        return;
      }

      await refreshCart(false);
      setOrderHasShipping(true);
    } catch (err) {
      console.error('apiUpdateShipment error', err);
      setSelectedShippingMethod(prevMethod);
      showToast('error', 'There was a problem updating your shipping method. Cart was refreshed.');
      await refreshCart(false);
    } finally {
      setIsUpdatingShipment(false);
    }
  };

  const renderCheckboxField = (
    id: string,
    boldLabel: boolean = false,
    isLink: boolean = false,
    href: string = '#',
    hrefLabel: string = '',
    onChange?: (checked: boolean) => void,
    defaultChecked: boolean = false,
  ) => (
    <div className={`formInput formCheckbox ${styles.checkbox} ${errors[id] ? 'formError' : ''}`} key={id}>
      <div>
        <input
          id={id}
          name={id}
          type='checkbox'
          defaultChecked={defaultChecked}
          aria-invalid={errors[id] ? 'true' : 'false'}
          aria-describedby={errors[id] ? `${id}-error` : undefined}
          onFocus={() => handleFocus(id)}
          disabled={isProcessing || isUpdatingSubstitutions}
          onChange={(e) => {
            if (onChange) onChange(e.target.checked);
            persistFormDraft();
          }}
        />
        <label htmlFor={id}>
          {boldLabel ? <strong>{LABELS[id]}</strong> : LABELS[id]}
          {isLink && <a href={href} target='_blank' rel='noopener noreferrer'>{hrefLabel}</a>}
        </label>
      </div>
      {errors[id] && <span id={`${id}-error`} className={styles.error}>{errors[id]}</span>}
    </div>
  );

  const renderTextareaField = (
    id: keyof typeof LABELS,
    autoComplete: string,
    placeholder?: string,
    defaultValue = '',
  ) => (
    <div className={`formInput formTextarea ${errors[id] ? 'formError' : ''}`} key={id}>
      <label htmlFor={id}>{LABELS[id]}</label>
      <textarea
        id={id}
        name={id}
        autoComplete={autoComplete}
        placeholder={placeholder}
        aria-invalid={errors[id] ? 'true' : 'false'}
        aria-describedby={errors[id] ? `${id}-error` : undefined}
        onFocus={() => handleFocus(id)}
        defaultValue={defaultValue}
        onChange={() => {
          markAccountFieldEdited(id);
          persistFormDraft();
        }}
        onBlur={() => persistFormDraft()}
      ></textarea>
      {errors[id] && <span id={`${id}-error`} className={styles.error}>{errors[id]}</span>}
    </div>
  );

  const renderSelectField = (
    id: keyof typeof LABELS,
    options: { label: string, value: string | number }[],
    defaultValue: string,
    onChange: (value: string | number) => void,
    placeholder: string = 'Select',
    classes: string,
    selectKey?: string,
  ) => (
    <div className={`formInput ${errors[id] ? 'formError' : ''}`} key={id}>
      <StylizedSelect
        key={selectKey}
        label={LABELS[id]}
        placeHolder={placeholder}
        name={id}
        styles={styles}
        options={options}
        onChange={onChange}
        defaultValue={defaultValue}
        classname={classes}
      />
      {errors[id] && <span id={`${id}-error`} className={styles.error}>{errors[id]}</span>}
    </div>
  );

  const renderRadioGroup = (
    id: keyof typeof LABELS,
    options: { label: string; value: string | number }[],
    defaultValue: string | number,
    onChange: (value: string | number) => void,
    hint: string = '',
    disabled: boolean = false
  ) => (
    <div className={`formInput ${errors[id] ? 'formError' : ''}`} key={id} role="group" aria-labelledby={`${id}-legend`}>
      <fieldset className={styles?.radioGroup}>
        <legend id={`${id}-legend`} className={styles?.label}>
          {LABELS[id]}
        </legend>

        {hint && <p className={styles?.hint}>{hint}</p>}

        <div className={styles?.radioOptions}>
          {options.map((opt) => {
            const inputId = `${String(id)}-${String(opt.value)}`;
            const radioLabelText = opt.label.replace(/<[^>]+>/g, '').replace(/&ndash;/g, '–').trim();
            return (
              <label key={inputId} htmlFor={inputId} className={styles?.radioItem}>
                <input
                  type="radio"
                  id={inputId}
                  name={String(id)}
                  value={String(opt.value)}
                  defaultChecked={String(defaultValue) === String(opt.value)}
                  aria-label={radioLabelText}
                  onChange={(e) => {
                    const v = e.target.value;
                    const parsed: string | number = typeof opt.value === 'number' ? Number(v) : v;
                    onChange(parsed);
                  }}
                  aria-describedby={errors[id] ? `${id}-error` : undefined}
                  className={styles?.radioInput}
                  disabled={disabled || isProcessing}
                />
                <span
                  className={styles?.radioLabel}
                  dangerouslySetInnerHTML={{ __html: opt.label }}
                />
              </label>
            );
          })}
        </div>
      </fieldset>

      {errors[id] && (
        <span id={`${id}-error`} className={styles.error}>
          {errors[id]}
        </span>
      )}
    </div>
  );

  const handleValidation = (): boolean => {
    if (formRef.current) {
      const formData = new FormData(formRef.current);
      const newErrors: Record<string, string> = {};

      // Required field validation
      REQUIRED_FIELDS.forEach((field) => {
        if (!formData.get(field)) {
          newErrors[field] = `${LABELS[field as keyof typeof LABELS]} is required`;
        }
      });

      const billingEmail = formData.get('billingEmail') as string;
      if (billingEmail && !REGEX.EMAIL.test(billingEmail)) {
        newErrors['billingEmail'] = 'Invalid email address';
      }

      const billingPhone = formData.get('billingPhoneNumber') as string;
      if (billingPhone && !isValidPhoneNumber(billingPhone, billingCountryCode)) {
        newErrors['billingPhoneNumber'] = 'Billing phone number is invalid';
      }

      const shippingPhone = formData.get('shippingPhoneNumber') as string;
      if (shippingPhone && !isValidPhoneNumber(shippingPhone, shippingCountryCode)) {
        newErrors['shippingPhoneNumber'] = 'Shipping phone number is invalid';
      }

      const billingPostCode = formData.get('billingPostCode') as string;
      if (billingPostCode && !getPostalCodePattern(billingCountryCode).test(billingPostCode)) {
        newErrors['billingPostCode'] = `Billing ${getPostalCodeLabel(billingCountryCode).toLowerCase()} is invalid`;
      }

      const billingProvince =
        (formData.get('billingProvinceCode') as string) || billingStateCode;
      if (isProvinceRequired(billingCountryCode) && !billingProvince?.trim()) {
        newErrors['billingProvinceCode'] = `${getProvinceLabel(billingCountryCode)} is required`;
      }

      // Resolve fulfillment details between the order data and the fulfillment state
      const noShipment = !orderData.shipments || orderData.shipments.length === 0;
      if (noShipment) {
        newErrors['noShipment'] = 'No shipment options are available at this time.';
      }

      if (!hasLockedShippingAddress) {
        newErrors['shippingStreet'] = 'A shipping address is required before checkout.';
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        console.error("Validation Errors", newErrors);
        return true;
      }
    }
    return false;
  };

  const getBillingContact = (formData: FormData): FulfillmentBillingContact => {
    return {
      firstName: formData.get('billingFirstName') as string,
      lastName: formData.get('billingLastName') as string,
      phoneNumber: formData.get('billingPhoneNumber') as string,
      email: formData.get('billingEmail') as string,
    };
  };

  const getShippingAddress = (formData: FormData, shippingStateCode: string): Address => {
    return {
      firstName: formData.get('shippingFirstName') as string,
      lastName: formData.get('shippingLastName') as string,
      street: formData.get('shippingStreet') as string,
      street2: formData.get('shippingStreet2') as string,
      city: formData.get('shippingCity') as string,
      phoneNumber: formData.get('shippingPhoneNumber') as string,
      provinceCode: formatProvinceCode(shippingCountryCode, shippingStateCode),
      postcode: formData.get('shippingPostCode') as string,
      countryCode: shippingCountryCode,
    };
  };

  const getBillingAddress = (formData: FormData, billingStateCode: string): Address => {
    const billingProvince = (formData.get('billingProvinceCode') as string) || billingStateCode;

    return {
      firstName: formData.get('billingFirstName') as string,
      lastName: formData.get('billingLastName') as string,
      street: formData.get('billingStreet') as string,
      street2: formData.get('billingStreet2') as string,
      city: formData.get('billingCity') as string,
      phoneNumber: formData.get('billingPhoneNumber') as string,
      provinceCode: formatProvinceCode(billingCountryCode, billingProvince),
      postcode: formData.get('billingPostCode') as string,
      countryCode: billingCountryCode,
    };
  };

  const hasAllNeededRequiredFields = (formData: FormData) => {
    const baseComplete = REQUIRED_FIELDS_WITHOUT_CHECK.every((field) => {
      const value = formData.get(field);
      if (typeof value === 'string') {
        return value.trim().length > 0;
      }
      return Boolean(value);
    });

    if (!baseComplete || !hasLockedShippingAddress) {
      return false;
    }

    if (isProvinceRequired(billingCountryCode)) {
      const province = (formData.get('billingProvinceCode') as string) || billingStateCode;
      return Boolean(province?.trim());
    }

    return true;
  };

  const syncCheckoutAddressesFromForm = useCallback(async (formData: FormData): Promise<boolean> => {
    if (!hasAllNeededRequiredFields(formData)) {
      return false;
    }

    const billingAddress = getBillingAddress(formData, billingStateCode);
    const shippingAddress = getShippingAddress(formData, shippingStateCode);
    const rawBillingPhone = (formData.get('billingPhoneNumber') as string) || '';
    const rawShippingPhone = (formData.get('shippingPhoneNumber') as string) || '';
    const billingPhoneDigits = normalizePhoneNumber(rawBillingPhone, billingCountryCode, 'digits');
    const shippingPhoneDigits = normalizePhoneNumber(rawShippingPhone, shippingCountryCode, 'digits');
    billingAddress.phoneNumber = billingPhoneDigits;
    shippingAddress.phoneNumber = shippingPhoneDigits;

    const shippingSnapshot = buildCheckoutShippingSnapshot(
      formData,
      shippingStateCode,
      shippingCountryCode,
    );
    const shippingChanged = shippingSnapshot !== lastSyncedShippingSnapshotRef.current;

    const orderResp = await setOrderAddress(
      orderState,
      (formData.get('billingEmail') as string) || session?.user?.email || "",
      shippingAddress,
      billingAddress,
      undefined,
      allowSubstitutions,
      orderData?.pricing?.appliedCouponCode || undefined,
    );

    if (!orderResp.success) {
      return false;
    }

    setOrderIsAddressed(true);

    // Billing-only edits still PUT ship-to; skip shipping re-quote when that snapshot is unchanged.
    if (shippingChanged) {
      const tokenValue = orderData?.token ?? orderState.token;
      const shipmentId = orderData?.shipments?.[0]?.id;
      const methodCode = selectedShippingMethod ?? orderData?.shipments?.[0]?.method;

      if (tokenValue && shipmentId && methodCode) {
        const shipmentResponse = await setOrderShippingMethod(tokenValue, shipmentId, methodCode);
        if (!shipmentResponse.success) {
          setOrderHasShipping(false);
          return false;
        }
        setOrderHasShipping(true);
      }

      lastSyncedShippingSnapshotRef.current = shippingSnapshot;
    }

    await refreshCart(false);

    return true;
  }, [
    allowSubstitutions,
    billingStateCode,
    billingCountryCode,
    orderData?.pricing?.appliedCouponCode,
    orderData?.shipments,
    orderData?.token,
    orderState,
    refreshCart,
    selectedShippingMethod,
    session?.user?.email,
    shippingCountryCode,
    shippingStateCode,
  ]);

  const syncCheckoutAddressesFromFormRef = useRef(syncCheckoutAddressesFromForm);
  syncCheckoutAddressesFromFormRef.current = syncCheckoutAddressesFromForm;

  useEffect(() => {
    if (!formRef.current || !hasLockedShippingAddress) return;
    const form = formRef.current;

    const checkRequiredFields = async () => {
      const currentCouponCode = orderData?.pricing?.appliedCouponCode;
      if (currentCouponCode !== lastCouponCodeRef.current) {
        lastCouponCodeRef.current = currentCouponCode;
        hasLoggedRequiredRef.current = false;
      }

      const formData = new FormData(form);
      const currentSignature = buildCheckoutAddressSignature(
        formData,
        billingStateCode,
        billingCountryCode,
        shippingStateCode,
        shippingCountryCode,
      );

      if (currentSignature === lastAddressSignatureRef.current && hasLoggedRequiredRef.current) {
        return;
      }
      lastAddressSignatureRef.current = currentSignature;

      if (hasAllNeededRequiredFields(formData)) {
        hasLoggedRequiredRef.current = true;

        // Cancel any queued update and schedule a fresh one with the latest form values.
        if (addressUpdateTimeoutRef.current) {
          clearTimeout(addressUpdateTimeoutRef.current);
        }
        const requestId = ++addressUpdateRequestIdRef.current;

        addressUpdateTimeoutRef.current = setTimeout(async () => {
          addressUpdateTimeoutRef.current = null;
          if (requestId !== addressUpdateRequestIdRef.current || !formRef.current) return;

          // Re-read the form at execution time so trailing debounce always sends newest values.
          const latestFormData = new FormData(formRef.current);
          if (!hasAllNeededRequiredFields(latestFormData)) return;

          setIsUpdatingAddress(true);
          try {
            const requestIdAtStart = requestId;
            const synced = await syncCheckoutAddressesFromFormRef.current(latestFormData);

            // Ignore stale responses from superseded requests.
            if (requestIdAtStart !== addressUpdateRequestIdRef.current) return;
            if (!synced) {
              setOrderIsAddressed(false);
            }
          } finally {
            if (requestId === addressUpdateRequestIdRef.current) {
              setIsUpdatingAddress(false);
            }
          }
        }, CHECKOUT_ADDRESS_SYNC_DEBOUNCE_MS);
      } else {
        // If fields become incomplete, cancel pending address writes from prior valid states.
        if (addressUpdateTimeoutRef.current) {
          clearTimeout(addressUpdateTimeoutRef.current);
          addressUpdateTimeoutRef.current = null;
        }
        // Invalidate any in-flight response so it cannot overwrite this newer local state.
        addressUpdateRequestIdRef.current += 1;
        hasLoggedRequiredRef.current = false;
        setIsUpdatingAddress(false);
      }
    };

    checkRequiredFieldsRef.current = checkRequiredFields;
    checkRequiredFields();

    form.addEventListener('input', checkRequiredFields);
    form.addEventListener('change', checkRequiredFields);

    return () => {
      // Prevent a delayed update from firing after listeners are removed/unmounted.
      if (addressUpdateTimeoutRef.current) {
        clearTimeout(addressUpdateTimeoutRef.current);
        addressUpdateTimeoutRef.current = null;
      }
      addressUpdateRequestIdRef.current += 1;
      setIsUpdatingAddress(false);
      form.removeEventListener('input', checkRequiredFields);
      form.removeEventListener('change', checkRequiredFields);
    };
  }, [
    billingCountryCode,
    billingStateCode,
    hasLockedShippingAddress,
    orderData?.pricing?.appliedCouponCode,
    shippingCountryCode,
    shippingStateCode,
  ]);

  const shippingDefaultsKey = useMemo(
    () => [
      shippingDefaults.street,
      shippingDefaults.street2,
      shippingDefaults.city,
      shippingDefaults.provinceCode,
      shippingDefaults.postcode,
      shippingCountryCode,
    ].join('|'),
    [
      shippingDefaults.street,
      shippingDefaults.street2,
      shippingDefaults.city,
      shippingDefaults.provinceCode,
      shippingDefaults.postcode,
      shippingCountryCode,
    ],
  );

  // Ship-to can change via estimate modal or cart refresh without a form input event.
  useEffect(() => {
    if (!formRef.current || !hasLockedShippingAddress) {
      return;
    }

    const formData = new FormData(formRef.current);
    const shippingSnapshot = buildCheckoutShippingSnapshot(
      formData,
      shippingStateCode,
      shippingCountryCode,
    );

    lastSyncedShippingSnapshotRef.current = shippingSnapshot;
    lastAddressSignatureRef.current = buildCheckoutAddressSignature(
      formData,
      billingStateCode,
      billingCountryCode,
      shippingStateCode,
      shippingCountryCode,
    );
    hasLoggedRequiredRef.current = true;
  }, [
    billingCountryCode,
    billingStateCode,
    hasLockedShippingAddress,
    shippingCountryCode,
    shippingDefaultsKey,
    shippingStateCode,
  ]);

  useEffect(() => {
    if (!checkRequiredFieldsRef.current) return;
    checkRequiredFieldsRef.current();
  }, [orderData?.pricing?.appliedCouponCode]);

  const showToast = (type: 'error' | 'success' | 'notice', message: string) => {
    const newToast = { id: uuidv4(), type, message };
    setToasts((prevToasts) => [...prevToasts, newToast]);
  };

  const handleDismiss = (id: string) => {
    setToasts((prevToasts) => prevToasts.filter(alert => alert.id !== id));
  };

  const handleSubmit = async (event?: FormEvent) => {
    if (isProcessing || isOrderUpdating) {
      return;
    }
    setIsProcessing(true);
    setPaymentError(false);
    if (event) event.preventDefault();

    if (!handleValidation()) {
      if (formRef.current) {
        setModalOpen(true);
        setSubmissionErrors(false);

        const formData = new FormData(formRef.current);

        const rawBillingPhone = (formData.get('billingPhoneNumber') as string) || '';
        const rawShippingPhone = (formData.get('shippingPhoneNumber') as string) || '';
        const billingPhoneDigits = normalizePhoneNumber(rawBillingPhone, billingCountryCode, 'digits');
        const shippingPhoneDigits = normalizePhoneNumber(rawShippingPhone, shippingCountryCode, 'digits');

        if (!billingPhoneDigits || !shippingPhoneDigits) {
          const newErrors: Record<string, string> = { ...errors };

          if (!billingPhoneDigits) newErrors.billingPhoneNumber = 'Billing phone number is invalid';
          if (!shippingPhoneDigits) newErrors.shippingPhoneNumber = 'Shipping phone number is invalid';

          setErrors(newErrors);
          setIsProcessing(false);
          setModalOpen(false);
          return;
        }

        const tokenValue = orderState.token;
        if (!tokenValue) {
          console.error('Exception: No Token found.');
          setIsProcessing(false);
          setModalOpen(false);
          setSubmissionErrors(true);
          return;
        }

        const paymentMethodCode = 'SPECS';

        const billingContact = getBillingContact(formData);
        const billingAddress = getBillingAddress(formData, billingStateCode);
        const shippingAddress = getShippingAddress(formData, shippingStateCode);

        billingContact.phoneNumber = billingPhoneDigits;
        billingAddress.phoneNumber = billingPhoneDigits;
        shippingAddress.phoneNumber = shippingPhoneDigits;

        if (addressUpdateTimeoutRef.current) {
          clearTimeout(addressUpdateTimeoutRef.current);
          addressUpdateTimeoutRef.current = null;
        }
        addressUpdateRequestIdRef.current += 1;

        setIsUpdatingAddress(true);
        try {
          const synced = await syncCheckoutAddressesFromForm(formData);
          if (!synced) {
            showToast('error', 'Could not save billing address. Please check your information and try again.');
            setIsProcessing(false);
            setModalOpen(false);
            setSubmissionErrors(true);
            return;
          }
        } finally {
          setIsUpdatingAddress(false);
        }

        if (!stripe || !elements) {
          console.error('Exception: Stripe or Elements not loaded');
          setIsProcessing(false);
          setModalOpen(false);
          setSubmissionErrors(true);
          return;
        }

        const prices = {
          buffer: orderData.pricing.buffer || 0,
          allowSubstitutions: !formData.get('noSubstitutions'),
          appliedCouponCode: orderData.pricing.appliedCouponCode,
          orderPricingData: orderData.pricing,
        };

        const shippingMethod =
          selectedShippingMethod
          ?? orderData.shipments?.[0]?.method
          ?? shippingMethodsForDisplay[0]?.code
          ?? PARTY_SUPPLIES_DOMESTIC_FEDEX_GROUND;

        const checkoutData = await handleCheckout(
          orderData.token,
          shippingAddress,
          paymentMethodCode,
          billingContact,
          billingAddress,
          formData,
          prices,
          session?.user.paymentKey,
          session?.token,
          session?.user?.email,
          elements,
          stripe,
          shippingMethod,
          orderIsAddressed,
          orderHasShipping,
        );

        if (checkoutData.showToast) {
          showToast('error', checkoutData.toastMessage ?? checkoutData.message);
        }

        if (checkoutData.errors) {
          sessionStorage.removeItem(OrderCompleteResponseKey);
          setIsProcessing(false);
          setModalOpen(false);
          if (checkoutData.paymentError) {
            setPaymentError(true);
          } else {
            setSubmissionErrors(true);
          }
          return;
        }
        if (checkoutData.complete) {
          setErrors({});
          clearCheckoutFormDraft(orderToken);
          sessionStorage.setItem(OrderCompleteResponseKey, encryptSessionData(JSON.stringify({
            // @ts-expect-error expoected error here...
            ...checkoutData.data.orderData,
            paymentIntentId: checkoutData.data?.paymentIntentId,
          })));
          setModalOpen(false);
          router.push('/checkout/order-received');
        } else {
          setIsProcessing(false);
          setModalOpen(false);
          console.error('Checkout Errors Found while completing the order.');
          setSubmissionErrors(true);
          return;
        }
      }
    } else {
      setIsProcessing(false);
      setModalOpen(false);
      console.error('Validation errors found.');
      setSubmissionErrors(true);
    }
  };

  const handleFocus = (field: string) => {
    setErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      delete newErrors[field];
      return newErrors;
    });
  };

  const paymentOptions: StripePaymentElementOptions = {
    fields: {
      billingDetails: {
        address: {
          postalCode: 'never',
          country: 'never'
        }
      }
    }
  };

  /**
   * Renders the section for shipping contact information.
   */
  const renderShippingContact = () => (
    <section
      key={`shipping-contact-${shippingDefaults.street}-${shippingDefaults.postcode}-${shippingDefaults.firstName}`}
      aria-labelledby='shipping-contact-heading'
    >
      <h2 id='shipping-contact-heading'>Shipping Contact</h2>
      {renderTextField(
        'shippingFirstName',
        'given-name',
        resolveCheckoutDraftString(formDraft, 'shippingFirstName', shippingDefaults.firstName || ''),
      )}
      {renderTextField(
        'shippingLastName',
        'family-name',
        resolveCheckoutDraftString(formDraft, 'shippingLastName', shippingDefaults.lastName || ''),
      )}
      {renderTextField(
        'shippingPhoneNumber',
        'tel',
        resolveCheckoutDraftString(formDraft, 'shippingPhoneNumber', shippingDefaults.phoneNumber || ''),
      )}
      <div className={styles.shippingAddressHeader}>
        <h2 id='shipping-address-heading'>Shipping Address</h2>
        <button
          type="button"
          className={styles.changeAddressLink}
          onClick={onOpenAddressModal}
        >
          Change address
        </button>
      </div>
      {hasLockedShippingAddress ? (
        <div className={styles.lockedShippingAddress}>
          <address>
            {shippingDefaults.street}
            {shippingDefaults.street2 ? `, ${shippingDefaults.street2}` : ''}
            <br />
            {shippingDefaults.city}
            {shippingProvinceLabel ? `, ${shippingProvinceLabel}` : ''}{' '}
            {formatPostcode(shippingDefaults.postcode || '', shippingCountryCode)}
            {!isUnitedStates(shippingCountryCode) && (
              <>
                <br />
                {shippingCountryLabel}
              </>
            )}
          </address>
        </div>
      ) : (
        <p className={styles.missingShippingAddress}>
          Add a shipping address to continue.{' '}
          <button type="button" onClick={onOpenAddressModal}>
            Enter your address
          </button>
        </p>
      )}
      <input type="hidden" name="shippingStreet" value={shippingDefaults.street || ''} />
      <input type="hidden" name="shippingStreet2" value={shippingDefaults.street2 || ''} />
      <input type="hidden" name="shippingCity" value={shippingDefaults.city || ''} />
      <input type="hidden" name="shippingPostCode" value={shippingDefaults.postcode || ''} />
    </section>
  );

  /**
   * Renders the section for shipping methods information.
   */
  const formatCurrency = (amountInCents: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })
      .format((amountInCents || 0) / 100);

  const renderShippingMethods = () => {
    // Defensive: handle missing shipments
    if (!orderData.shipments || orderData.shipments.length === 0) {
      return (
        <section aria-labelledby="shipping-methods-heading">
          <h2 id="shipping-methods-heading">Shipping Methods</h2>
          <p className={styles?.hint}>
            No shipping methods are currently available for your address.
          </p>
        </section>
      );
    }

    const shipment = orderData.shipments[0];

    // Prefer local selection, otherwise the method already on the order
    const preselected = selectedShippingMethod ?? shipment.method ?? "";
    const hasMethodsToShow = shippingMethodsForDisplay.length > 0;
    const isInitialShippingMethodsLoad = !hasLoadedShippingMethods;
    const isRefreshingShippingMethods = shippingMethodsLoading && hasLoadedShippingMethods && hasMethodsToShow;
    const showEmptyState = hasLoadedShippingMethods && !shippingMethodsLoading && !hasMethodsToShow;

    return (
      <section aria-labelledby="shipping-methods-heading">
        <h2 id="shipping-methods-heading">Shipping Methods</h2>

        <div
          className={styles.shippingMethodsContent}
          aria-busy={isInitialShippingMethodsLoad || isRefreshingShippingMethods}
        >
          {isInitialShippingMethodsLoad && (
            <div className={styles.shippingMethodsInitialLoader}>
              <Spinner />
            </div>
          )}

          {showEmptyState && (
            <p className={styles?.hint}>
              No shipping methods are currently available for your address.
            </p>
          )}

          {hasMethodsToShow &&
            renderRadioGroup(
              "shippingMethod",
              shippingMethodsForDisplay.map((method) => {
                const name = method.name || getShippingMethodDisplayName(method.code) || 'Shipping Method';
                const priceInCents = method.price ?? 0;
                const priceLabel = priceInCents > 0
                  ? `&ndash; ${formatCurrency(priceInCents)}`
                  : '';
                return {
                  label: `${name} <strong>${priceLabel}</strong>`,
                  value: method.code,
                };
              }),
              preselected,
              (value: string | number) => {
                const code = String(value);
                setSelectedShippingMethod(code);
                handleShippingMethodChange(code);
              },
              "Select a shipping method",
              isOrderUpdating,
            )}

          {isRefreshingShippingMethods && hasMethodsToShow && (
            <div className={styles.shippingMethodsLoader}>
              <Spinner />
            </div>
          )}
        </div>
      </section>
    );
  };

  /**
   * Renders the section for additional order options, such as substitutions and gift options.
   */
  const handleSubstitutionsChange = async (noSubstitutionsChecked: boolean) => {
    const tokenValue = orderData?.token ?? orderState.token;
    if (!tokenValue) {
      return;
    }

    markPartyOrderNoSubstitutions(tokenValue, noSubstitutionsChecked);
    setIsUpdatingSubstitutions(true);

    try {
      const response = await setOrderDetails(tokenValue, orderState, orderDispatch, {
        allowSubstitutions: !noSubstitutionsChecked,
      });

      if (!response.success) {
        showToast('error', 'Could not update substitution preference. Please try again.');
        return;
      }

      await refreshCart(false);
    } finally {
      setIsUpdatingSubstitutions(false);
    }
  };

  const renderAdditionalOptions = () => (
    <section
      key={`additional-options-${allowSubstitutions}-${orderData.pricing?.buffer ?? 0}`}
      aria-labelledby='additional-options-heading'
    >
      <h2 id='additional-options-heading'>Additional Options</h2>
      {renderCheckboxField(
        'noSubstitutions',
        true,
        false,
        '#',
        '',
        handleSubstitutionsChange,
        !allowSubstitutions,
      )}
      <div className={styles.orderNotes}>
        {renderTextareaField(
          'orderNotes',
          'off',
          'Notes about your order, e.g. special notes for delivery.',
          resolveCheckoutDraftString(formDraft, 'orderNotes'),
        )}
      </div>
    </section>
  );

  /**
   * Renders the billing contact section, including name, phone number, and email.
   */
  const renderBillingContact = () => (
    <section aria-labelledby='billing-contact-heading'>
      <h2 id='billing-contact-heading'>Billing Contact</h2>
      {renderTextField(
        'billingFirstName',
        'given-name',
        resolveCheckoutDraftString(formDraft, 'billingFirstName', session?.user?.billingAddress?.firstName || ''),
      )}
      {renderTextField(
        'billingLastName',
        'family-name',
        resolveCheckoutDraftString(formDraft, 'billingLastName', session?.user?.billingAddress?.lastName || ''),
      )}
      {renderTextField(
        'billingPhoneNumber',
        'tel',
        resolveCheckoutDraftString(formDraft, 'billingPhoneNumber', session?.user?.billingAddress?.phoneNumber || ''),
      )}
      {renderTextField(
        'billingEmail',
        'email',
        resolveCheckoutDraftString(formDraft, 'billingEmail', session?.user?.billingAddress?.email || ''),
      )}
      <h2 id='billing-address-heading'>Billing Address</h2>
      <div className={formStyles.columnsWideNarrow}>
        {renderTextField(
          'billingStreet',
          'address-line1',
          resolveCheckoutDraftString(formDraft, 'billingStreet', session?.user?.billingAddress?.street || ''),
        )}
        {renderTextField(
          'billingStreet2',
          'address-line2',
          resolveCheckoutDraftString(formDraft, 'billingStreet2', session?.user?.billingAddress?.street2 || ''),
        )}
      </div>
      <div className={formStyles.columnsLocation}>
        {renderTextField(
          'billingCity',
          'address-level2',
          resolveCheckoutDraftString(formDraft, 'billingCity', session?.user?.billingAddress?.city || ''),
        )}
        {billingProvinceOptions.length > 0 ? (
          renderSelectField(
            'billingProvinceCode',
            billingProvinceOptions,
            billingStateCode,
            (value: string | number) => {
              const nextStateCode = value as string;
              setBillingStateCode(nextStateCode);
              markAccountFieldEdited('billingProvinceCode');
              persistFormDraft({ billingStateCode: nextStateCode });
            },
            getProvinceLabel(billingCountryCode),
            'checkout',
            `billing-province-${billingCountryCode}-${billingStateCode}-${accountSelectRevision}`,
          )
        ) : (
          renderTextField(
            'billingProvinceCode',
            'address-level1',
            resolveCheckoutDraftString(formDraft, 'billingProvinceCode', billingStateCode),
            'text',
          )
        )}
        {renderTextField(
          'billingPostCode',
          'postal-code',
          resolveCheckoutDraftString(formDraft, 'billingPostCode', session?.user?.billingAddress?.postcode || ''),
          'text',
        )}
      </div>
      <div className={formStyles.fullWidthField}>
        {renderSelectField(
          'billingCountryCode',
          COUNTRY_CODES,
          billingCountryCode,
          (value: string | number) => {
            const countryCode = String(value).toUpperCase();
            const nextStateCode = isUnitedStates(countryCode) ? 'TX' : '';
            setBillingCountryCode(countryCode);
            setBillingStateCode(nextStateCode);
            markAccountFieldEdited('billingCountryCode');
            markAccountFieldEdited('billingProvinceCode');
            persistFormDraft({ billingCountryCode: countryCode, billingStateCode: nextStateCode });
          },
          'Select a country',
          'checkout',
          `billing-country-${billingCountryCode}-${accountSelectRevision}`,
        )}
      </div>
    </section>
  );

  /**
   * Renders the payment method selection using Stripe PaymentElement.
   */
  const renderPaymentMethod = () => (
    <section aria-labelledby='payment-method-heading' className={styles.checkoutPayment} >
      <h2 id='payment-method-heading'>Payment Method</h2>
      <PaymentElement options={paymentOptions} />
    </section >
  );

  return (
    <>
      <form
        key={`checkout-form-${orderToken ?? 'pending'}`}
        ref={formRef}
        onChange={() => persistFormDraft()}
        onSubmit={(e) => {
        e.preventDefault();
        debounceClick(handleSubmit);
      }}>
        {alerts.length > 0 && (
          <div className={styles.alertWrapper} aria-live='assertive'>
            {alerts.map((alert) => (
              <Toast
                key={alert.id}
                id={alert.id}
                type={alert.type}
                message={alert.message}
                onDismiss={() => handleDismiss(alert.id)}
                dismissible
                fadeOutTime={5000}
              />
            ))}
          </div>
        )}
        <div className={styles.checkout}>
          <div className={styles.checkoutDetails}>
            {status !== 'authenticated' && <CartCheckoutLogin refreshCart={refreshCart} />}
            <div className={styles.columns}>
              <div className={styles.column}>
                {renderShippingMethods()}
                {renderShippingContact()}
                {renderAdditionalOptions()}
              </div>
              <div className={styles.column}>
                {renderBillingContact()}
              </div>
            </div>
          </div>
          <div className={styles.checkoutSidebar}>
            <CartCheckoutCoupons setOrderData={setOrderData} orderData={orderData} />
            <section className={styles.wrapper}>
              <div className={styles.table}>
                {isClient &&
                <CartCheckoutTotals
                  className='showEditCart'
                  showBufferTotal
                  pricing={orderData.pricing}
                  state={orderData?.states?.checkout}
                  orderData={orderData}
                  loading={isOrderUpdating}
                  setIsModalOpen={onOpenAddressModal}
                  onEditCart={onEditCart}
                  onCouponRemoved={async () => await refreshCart(false)}
                />
                }
                {renderPaymentMethod()}
                <div className={styles.checkboxes}>
                  {renderCheckboxField(
                    'ageCheck',
                    true,
                    false,
                    '#',
                    '',
                    undefined,
                    resolveCheckoutDraftCheckbox(formDraft, 'ageCheck', false),
                  )}
                  {renderCheckboxField(
                    'termsCheck',
                    false,
                    true,
                    '/legal/terms-conditions',
                    'Terms & Conditions',
                    undefined,
                    resolveCheckoutDraftCheckbox(formDraft, 'termsCheck', false),
                  )}
                </div>
                <StylizedButton type='submit' disabled={isProcessing || isOrderUpdating || !hasLockedShippingAddress || shippingUnavailable}>
                  {isProcessing ? <Spinner /> : 'Place Order'}
                </StylizedButton>
                {shippingUnavailable && (
                  <p className={styles.submissionError}>
                    Shipping is unavailable for this order. An item may be too large to ship or cannot ship to your address.
                  </p>
                )}
                {submissionErrors && (
                  <p className={styles.submissionError}>An unexpected error has occurred.<br />Please try again.</p>
                )}
                {paymentError && (
                  <p className={styles.submissionError}>A payment error has occurred. Please check your payment details and try again.</p>
                )}
              </div>
            </section>
            <CartCheckoutDisclosures />
            <CartCheckoutTemporaryAuthorization />
          </div>
        </div>
      </form>
      <Modal content={<CheckoutModal onClose={() => setModalOpen(false)} />} enableCloseButton={false} isOpen={modalOpen} onClose={() => setModalOpen(false)} />
    </>
  );
}
